import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { RootState } from '../../store/store';
import { logout } from '../../store/slices/authSlice';
import { getLoginPath } from '../utils/authRedirect';

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    const validateToken = () => {
      if (!token) return;

      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        
        if (payload.exp < currentTime) {
          console.log('Token expired on app load, logging out');
          dispatch(logout());
          const redirectPath = getLoginPath(user?.userType as 'student' | 'tutor' | null);
          navigate(redirectPath);
        } else {
          const timeUntilExpiry = payload.exp - currentTime;
          const expiryHours = Math.floor(timeUntilExpiry / 3600);
          const expiryMinutes = Math.floor((timeUntilExpiry % 3600) / 60);
          
          console.log(`Token valid. Expires in ${expiryHours}h ${expiryMinutes}m`);
          
          if (user) {
            console.log('Auto-login successful:', {
              email: user.email,
              userType: user.userType,
              userId: user.id
            });
          }
        }
      } catch (error) {
        console.error('Invalid token format:', error);
        dispatch(logout());
        const redirectPath = getLoginPath(null);
        navigate(redirectPath);
      }
    };

    validateToken();

    const tokenCheckInterval = setInterval(validateToken, 60000);

    return () => clearInterval(tokenCheckInterval);
  }, [token, user, dispatch, navigate]);

  return <>{children}</>;
};
